import { Box, Text, ThemeUICSSObject } from 'theme-ui';
import { Tooltip } from 'react-tooltip';

import { joinNames } from '@/components/Collection/BannerCoverageNote';

type Market = { id: string; name: string };

export type MarketCoverageSummary = {
  inline: string;
  overflowNames?: string[];
  // Everything in `inline` before the "+k more" tag, pre-split so the
  // renderer never has to reverse-engineer it out of the joined string.
  overflowPrefix?: string;
};

// Below this many markets, list every name directly instead of folding into
// "+k more" — a reasonable default per the PRP, open to tuning against real data.
const DIRECT_LIST_THRESHOLD = 2;

// Neither query orders its warehouse list, and `cache-and-network` can
// return the same set in a different order on a background refetch. Without
// this, which two names get promoted into the always-visible summary (vs.
// folded into "+k more") could change on a row whose coverage never did.
const byName = (a: Market, b: Market) => a.name.localeCompare(b.name);

/**
 * Picks whichever side of the coverage split — covered or uncovered — is
 * shorter and shows that inline, so near-full and sparse coverage never need
 * the tooltip just to be scannable. Checks the near-full case first since a
 * tie (both sides at the threshold) should read as "all but", not a direct list.
 */
export const summarizeMarketCoverage = (
  covered: Market[],
  allWarehouses: Market[],
): MarketCoverageSummary => {
  // Scoped to markets that actually exist in `allWarehouses`: a covered id
  // IMS hasn't caught up on retiring (no delete handling on its warehouse
  // sync) would otherwise inflate `count` past what `uncovered` can
  // complement, producing a ratio and an "all but" list that contradict
  // each other.
  const sortedCovered = covered
    .filter((c) => allWarehouses.some((w) => w.id === c.id))
    .sort(byName);
  const total = allWarehouses.length;
  const count = sortedCovered.length;
  const base = `(${count}/${total} Markets)`;

  if (count === 0 || count === total) {
    return { inline: base };
  }

  const uncovered = allWarehouses
    .filter((warehouse) => !sortedCovered.some((c) => c.id === warehouse.id))
    .sort(byName);

  if (uncovered.length <= DIRECT_LIST_THRESHOLD) {
    return {
      inline: `${base} · all but ${joinNames(uncovered.map((w) => w.name))}`,
    };
  }

  // Not `joinNames`: a direct list reads as an inventory of names, not a
  // sentence, and the "all but" branch above is the one place natural
  // "A, B and C" phrasing actually reads better.
  if (count <= DIRECT_LIST_THRESHOLD) {
    return {
      inline: `${base} · ${sortedCovered.map((c) => c.name).join(', ')}`,
    };
  }

  const [first, second] = sortedCovered;
  const overflowPrefix = `${base} · ${first.name}, ${second.name}`;
  return {
    inline: `${overflowPrefix} +${count - DIRECT_LIST_THRESHOLD} more`,
    overflowNames: sortedCovered.map((c) => c.name),
    overflowPrefix,
  };
};

const tooltipStyle: ThemeUICSSObject = {
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.25)',
  maxWidth: '250px',
};

const tooltipPopoverStyle: ThemeUICSSObject = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  rowGap: 2,
};

const overflowTriggerStyle: ThemeUICSSObject = {
  cursor: 'pointer',
  textDecoration: 'underline dotted',
};

/**
 * Renders a Markets/Banner cell's coverage summary. Only the overflow case
 * ("+k more") hides anything, so it's the only case with a tooltip trigger.
 * This page's table rows navigate on click with no `stopPropagation`
 * (unlike the `MarketFulfillmentWarehouse` precedent, whose table has no
 * row click handler), so the trigger stops its own click from bubbling to
 * the row, and `clickable={false}` keeps the popup itself non-interactive.
 * `suffix` lets the Banner column append its own " + default" note after
 * the coverage summary without duplicating this component's rendering.
 */
export function MarketsCoverageCell({
  covered,
  allWarehouses,
  tooltipId,
  suffix,
}: {
  covered: Market[];
  allWarehouses: Market[];
  tooltipId: string;
  suffix?: string;
}) {
  const { inline, overflowNames, overflowPrefix } = summarizeMarketCoverage(
    covered,
    allWarehouses,
  );

  if (!overflowNames) {
    return (
      <Text>
        {inline}
        {suffix}
      </Text>
    );
  }

  return (
    <>
      <Text>
        {overflowPrefix}
        <Text
          as="span"
          data-tooltip-id={tooltipId}
          sx={overflowTriggerStyle}
          onClick={(event) => event.stopPropagation()}
        >
          {` +${overflowNames.length - DIRECT_LIST_THRESHOLD} more`}
        </Text>
        {suffix}
      </Text>
      <Tooltip
        id={tooltipId}
        variant="light"
        clickable={false}
        sx={tooltipStyle}
        className="opaque-tooltip"
      >
        <Box sx={tooltipPopoverStyle}>{overflowNames.join(', ')}</Box>
      </Tooltip>
    </>
  );
}
